import './popup-transfer.ajscss';

import { MailBasketAction } from '@agent/core/basket';
import { IBasketStore } from '@agent/core/basket/basket.store';
import { ITransfer, TransferKey } from '@agent/core/contact/transfer/models/transfer';
import { ITransferDeprecatedService } from '@agent/core/contact/transfer/transfer-deprecated.service';
import { IComponentController, IComponentOptions } from 'angular';
import { i18n } from 'i18next';
import { map, reduce, tap } from 'rxjs';

import { Channel, ChannelType, DisplayDeprecatedComponent, LoaderDeprecatedService, ToasterFacade } from '@contact/upgrade';

import templateUrl from './popup-transfer.html';

const MODULE_NAME = 'popupTransfer';
export type CsrFilter = {
  csrPrefix?: string;
  teamId?: string;
  groupId?: string;
  skillIds?: string[];
};

class PopupTransferCtrl extends DisplayDeprecatedComponent implements IComponentController {
  public name = MODULE_NAME;

  // binding
  public dismiss: Function;
  public resolve: Function;

  // internal properties
  public key: TransferKey;
  public channel: Channel;
  public transfer: ITransfer;
  public containerIds: string[];
  public valid: boolean;

  constructor(
    private readonly $i18next: i18n,
    private readonly toasterFacade: ToasterFacade,
    private readonly transferDeprecatedService: ITransferDeprecatedService,
    private readonly basketStore: IBasketStore,
    private readonly loaderDeprecatedService: LoaderDeprecatedService
  ) {
    super();
  }

  $onInit(): void {
    let param = this.resolve();
    this.key = param.key;
    this.channel = param.channel;
    this.containerIds = param.loaderContainerIds;
    // initialize transfer element
    this.transfer = this.transferDeprecatedService
      .getTransfer(this.key)
      .getOrElseFunc(() => this.transferDeprecatedService.createTransfer(this.key, this.channel));
  }

  validate(): void {
    // check if form is completed
    if (!this.valid) {
      this.toasterFacade.alert({ message: this.$i18next.t('agent:contact.workspace.transfer.toast.validate_error') });
      return;
    }
    // active loader
    // list of container ids
    this.loaderDeprecatedService.active(...this.containerIds);
    // close popin
    this.dismiss();
    this.toasterFacade.notification({
      message: this.$i18next.t('agent:contact.workspace.transfer.toast.process'),
      duration: 2000,
    });
    // validate transfer
    switch (this.transfer.channel.type) {
      case ChannelType.MAIL:
        this.transferDeprecatedService
          .validateTransfer$(this.transfer.key)
          .pipe(
            map(result => {
              let [requestId, success] = result;
              if (!success) {
                this.basketStore.dispatch(new MailBasketAction.SetError([requestId]));
              }
              return success ? 0 : 1;
            }),
            reduce((acc, v) => acc + v, 0),
            tap(nbErrors => {
              // at the end, a toast
              if (nbErrors > 0) {
                this.toasterFacade.alert({
                  message: this.$i18next.t('agent:contact.workspace.basket.toast.error.generic_error_with_count', {
                    nbErrors: nbErrors,
                    nbMax: this.transfer.key.length,
                  }),
                });
              } else {
                this.toasterFacade.notification({
                  message: this.$i18next.t('agent:contact.workspace.basket.toast.transfer.complete'),
                  duration: 2000,
                });
              }
              // remove the loader
              this.loaderDeprecatedService.dispose(...this.containerIds);
              // refresh basket
              setTimeout(() => this.basketStore.dispatch(new MailBasketAction.ManualRefresh()), 1000);
            })
          )
          .subscribe();
        break;

      case ChannelType.PHONE:
      case ChannelType.UNITARY_PHONE:
        this.transferDeprecatedService
          .validateTransfer(this.transfer.key)
          .then(() =>
            this.toasterFacade.notification({
              message: this.$i18next.t('agent:contact.workspace.transfer.toast.complete'),
              duration: 2000,
            })
          )
          .finally(() => this.loaderDeprecatedService.dispose(...this.containerIds));
        break;

      default:
        break;
    }
  }

  cancel(): void {
    this.dismiss();
  }

  // get transfer from event object
  onTransferChange({ transfer }) {
    this.transfer = transfer;
    this.transferDeprecatedService.updateTransfer(transfer);
    this.valid = this.transferDeprecatedService.canValidateTransfer(this.transfer.key, this.transfer.channel.type);
  }
}

export class PopupTransfer implements IComponentOptions {
  templateUrl = templateUrl;
  bindings = {
    resolve: '&',
    dismiss: '&',
  } as { [binding: string]: string };
  controller = ['$i18next', 'toasterFacade', 'transferDeprecatedService', 'basketStore', 'loaderDeprecatedService', PopupTransferCtrl];
}

export default MODULE_NAME;
